import { orderBy } from 'lodash';
import { Image, Price, StrainInfo } from '@/components';
import { currencyFormat, formatWeightLabel } from '@/utils';
import type { LineItem, Variant } from '__generated__/graphql';

import styles from './orderdetail.module.scss';

type OrderDetailLineItem = LineItem & {
  flower_type?: string | null;
  brand_name?: string | null;
  subcategory?: string | null;
};

interface ItemListProps {
  items: Array<LineItem | null | undefined>;
}

function ItemList({ items }: ItemListProps) {
  const sortedItems = orderBy(items, ['name'], ['desc']);

  const renderItemCard = (item: LineItem, index: number) => {
    const itemData = item as OrderDetailLineItem;
    const variants = (itemData?.variants ?? []).filter(
      (v): v is Variant => !!v,
    );
    const firstVariant = variants?.[0];
    const salePrice =
      firstVariant?.price ?? itemData?.sale_price ?? itemData?.price ?? 0;
    const basePrice = itemData?.price ?? salePrice;
    const isSpecial = basePrice !== salePrice;
    const brandName = itemData?.brand_name ?? itemData?.brand;
    const subCategory = itemData?.subcategory ?? itemData?.category;
    const flowerType = itemData?.flower_type;
    const weightLabel = variants
      ?.map(v => formatWeightLabel(v?.type ?? ''))
      .filter(Boolean)
      .join(', ');
    const REVERSE_PROXY_LINK = process.env.NEXT_PUBLIC_REVERSE_PROXY_LINK ?? '';

    return (
      <div
        className={styles['order__items-card']}
        key={itemData?.item_id ?? itemData?.id ?? `${itemData?.name}-${index}`}
      >
        <figure className={styles['order__items-image']}>
          <Image
            alt={itemData?.name ?? 'Picture'}
            fallback={`${REVERSE_PROXY_LINK}/images/product-placeholder.svg`}
            fill
            src={itemData?.thumbnail ?? ''}
            title={itemData?.name ?? 'Picture'}
          />
        </figure>

        <div className={styles['order__items-info']}>
          <div className={styles['order__items-description']}>
            {(flowerType || brandName || subCategory) && (
              <div className={styles['order__items-tags']}>
                {flowerType ? (
                  <StrainInfo
                    className={styles['order__items-flower-type']}
                    strain={flowerType}
                  />
                ) : null}
                {brandName ? (
                  <span className={styles['order__items-tag']}>
                    {brandName}
                  </span>
                ) : null}
                {subCategory ? (
                  <span
                    className={`${styles['order__items-tag']} ${styles['order__items-subcategory']}`}
                  >
                    {subCategory}
                  </span>
                ) : null}
              </div>
            )}
            <p className={styles['order__items-name']}>{itemData?.name}</p>
            {!!weightLabel && (
              <p className={styles['order__items-weight']}>{weightLabel}</p>
            )}
          </div>

          <div className={styles['order__items-price-wrap']}>
            <span className={styles['order__item-quantity']}>
              {`X${itemData?.quantity ?? 0}`}
            </span>
            <Price
              className={styles['order__item-price']}
              price={`$${currencyFormat(basePrice)}`}
              salePrice={`$${currencyFormat(salePrice)}`}
              special={isSpecial}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles['order__items-list']}>
      {sortedItems.map((item, index) =>
        item ? renderItemCard(item, index) : null,
      )}
    </div>
  );
}

export default ItemList;
